import { Link, useLocation, useNavigate } from "react-router-dom";
import { PersonaSwitcher } from "@/components/PersonaSwitcher";
import { useSession, ROLE_LABEL } from "@/context/SessionContext";
import { RotateCcw } from "lucide-react";

function ResetButton() {
  const { resetSession } = useSession();
  const nav = useNavigate();
  return (
    <button
      onClick={() => { resetSession(); nav("/"); }}
      data-testid="reset-demo"
      className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-mill-muted transition-colors hover:text-mill-ink"
    >
      <RotateCcw className="h-3.5 w-3.5" /> Reset demo / change role
    </button>
  );
}

function Brand({ subtitle }) {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-mill-moss font-display text-lg font-bold text-white">M</span>
      <span className="hidden leading-tight sm:block">
        <span className="block font-display text-sm font-bold text-mill-ink">The Mill</span>
        <span className="block text-[11px] tracking-wide text-mill-muted">{subtitle}</span>
      </span>
    </Link>
  );
}

export function Nav() {
  const { pathname } = useLocation();
  const { session } = useSession();

  // Simple demonstrator — clean, minimal chrome.
  if (pathname === "/") {
    return (
      <header className="sticky top-0 z-30 h-14 border-b border-stone-200/70 bg-mill-bg/85 backdrop-blur-xl" data-testid="nav-demo">
        <div className="mx-auto flex h-14 max-w-2xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-mill-moss font-display font-bold text-white">M</span>
            <span className="font-display text-sm font-bold text-mill-ink">The Mill</span>
          </div>
          <span className="text-[11px] uppercase tracking-wider text-mill-muted">Simple demonstration</span>
        </div>
      </header>
    );
  }

  // Private volunteer workspace / invite — minimal chrome.
  if (pathname.startsWith("/invite/") || pathname === "/me") {
    return (
      <header className="sticky top-0 z-30 border-b border-stone-200/70 bg-mill-bg/85 backdrop-blur-xl" data-testid="nav-minimal">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-2.5 px-5 py-3 sm:px-8">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-mill-moss font-display text-lg font-bold text-white">M</span>
            <span className="leading-tight">
              <span className="block font-display text-sm font-bold text-mill-ink">The Mill</span>
              <span className="block text-[11px] tracking-wide text-mill-muted">Your Mill Card</span>
            </span>
          </div>
          {pathname === "/me" && session && <ResetButton />}
        </div>
      </header>
    );
  }

  // Staff workspace.
  if (pathname.startsWith("/staff")) {
    return (
      <header className="sticky top-0 z-30 border-b border-stone-200/70 bg-mill-bg/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
          <Brand subtitle="Staff workspace" />
          <div className="flex items-center gap-3">
            {session && <span className="hidden text-xs font-medium text-mill-muted sm:inline" data-testid="nav-role">{ROLE_LABEL[session.role]}</span>}
            {session && <ResetButton />}
          </div>
        </div>
      </header>
    );
  }

  const isReference = pathname.startsWith("/journey") || pathname.startsWith("/reference") || pathname.startsWith("/overview");

  return (
    <header className="sticky top-0 z-30 border-b border-stone-200/70 bg-mill-bg/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
        <Brand subtitle="People · Places · Practice" />
        <nav className="hidden items-center gap-0.5 md:flex">
          {[
            { to: "/", label: "Home" },
            { to: "/explore", label: "What's on" },
            { to: "/staff", label: "Staff" },
            { to: "/overview", label: "About" },
          ].map((l) => {
            const active = l.to === "/" ? pathname === "/" : pathname.startsWith(l.to);
            return (
              <Link key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? "bg-mill-moss text-white" : "text-mill-muted hover:bg-mill-alt hover:text-mill-ink"}`}>
                {l.label}
              </Link>
            );
          })}
        </nav>
        <div className="flex items-center gap-3">
          {isReference && <PersonaSwitcher />}
          {!isReference && session && <ResetButton />}
        </div>
      </div>
    </header>
  );
}
